import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PinAction, SliderCheckbox, SubmissionButtons } from '@components/common';
import NestedCheckboxGroup from '@components/common/NestedCheckboxGroup';

import { useQueryCellsUsedBands } from '@hooks/cells/useQueryCellsUsedBands';
import { useQueryAllSectors } from '@hooks/sectors/useQuerySectors';
import { useManageCellOptions } from '@hooks/useManageCellOptions';

import { map } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	clearSelectedSiteIdsToRenderSector,
	resetRightSideControls,
	setSectorsFilters,
	toggleSectors,
} from '@slices/map';

import { isEqual } from '@utils/common';
import { isEmptyObjectRecursive } from '@utils/objects/isEmptyObjectRecursive';
import { prepareNestedCheckboxes } from '@components/Map/MapControls/Controls/Filters/helpers/prepareCheckboxItems';

import { ISectorsFilterState } from '@interfaces/map/sectors';
import { ICheckboxGroup } from '@components/common/NestedCheckboxGroup/NestedCheckboxGroup.types';

import { TTechnologiesValues } from '@enums/common';

import styles from './Sectors.module.scss';
import controlStyles from '@components/Map/MapControls/MapControls.module.scss';

const Sectors = () => {
	const dispatch = useAppDispatch();

	const {
		pinnedFilter,
		sectors: {
			isSectorsShown,
			filters: appliedSectorsFilters,
		},
		appliedSitesFiltersValues,
		areBoroughsEnabled,
		showingCustomPolygonIds,
	} = useAppSelector(map);

	const {
		band4GFddOptions,
		band4GTddOptions,
		band5GOptions,
		isLoadingTechnologiesOptions,
	} = useManageCellOptions();

	const { allSectors } = useQueryAllSectors();

	const { cellsUsedBands, isLoading: isUsedBandsLoading } = useQueryCellsUsedBands();

	const containerRef = useRef<HTMLDivElement>(null);

	const [technologyCheckboxes, setTechnologyCheckboxes] = useState<ICheckboxGroup[]>([]);
	const [temporaryCheckboxesState, setTemporaryCheckboxesState] = useState<ICheckboxGroup[]>([]);
	const [sectorsDisabledTooltipText, setSectorsDisabledTooltipText] = useState('');
	const [filters, setFilters] = useState<ISectorsFilterState | null>(null);

	const [isSectorsVisible, setIsSectorsVisible] = useState(isSectorsShown);
	const [isDirtySectorsVisible, setIsDirtySectorsVisible] = useState(false);

	const [isSectorsDisabled, setIsSectorsDisabled] = useState(false);
	const [isPinned, setIsPinned] = useState(false);

	const handleChangeSectorsState = useCallback(() => {
		setIsSectorsVisible(prevIsSectorsVisible => !prevIsSectorsVisible);

		setIsDirtySectorsVisible(true);
	}, []);

	const applyFilters = useCallback(() => {
		dispatch(toggleSectors(isSectorsVisible));

		if (isSectorsVisible) {
			dispatch(clearSelectedSiteIdsToRenderSector());
		}

		if (filters) {
			dispatch(setSectorsFilters(filters));
		}

		if (!pinnedFilter) {
			dispatch(resetRightSideControls());
		}
	}, [
		dispatch,
		isSectorsVisible,
		pinnedFilter,
		filters,
	]);

	const handleReset = useCallback(() => {
		dispatch(setSectorsFilters(null));
		dispatch(toggleSectors(false));
		dispatch(resetRightSideControls());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (isLoadingTechnologiesOptions || isUsedBandsLoading || technologyCheckboxes.length) {
			return;
		}

		setTechnologyCheckboxes(prepareNestedCheckboxes({
			bands: band4GTddOptions.concat(band4GFddOptions).concat(band5GOptions),
			currentFilterState: appliedSectorsFilters,
			usedBands: cellsUsedBands,
			sectorSettings: allSectors,
			filteredTechnologiesOnMap: appliedSitesFiltersValues.technologies,
		}));
	}, [
		allSectors,
		appliedSectorsFilters,
		appliedSitesFiltersValues,
		band4GFddOptions,
		band4GTddOptions,
		band5GOptions,
		cellsUsedBands,
		isLoadingTechnologiesOptions,
		isUsedBandsLoading,
		technologyCheckboxes.length,
	]);

	const handleChangeSectorsTechnology = useCallback((items: ICheckboxGroup[]) => {
		setTemporaryCheckboxesState(items);

		setFilters(() => {
			return items.reduce((acc: ISectorsFilterState, item) => {
				if (item.checked || item.isIndefinite) {
					acc.technologies.push(item.value as TTechnologiesValues);
				}

				if ((item.checked || item.isIndefinite) && item.nestedItems?.length) {
					item.nestedItems.forEach(nestedItem => {
						if (nestedItem.checked) {
							acc.bands.push(nestedItem.value);
						}
					});
				}

				return acc;
			}, { technologies: [], bands: [] });

		});
	}, [setFilters]);

	useEffect(() => {
		setSectorsDisabledTooltipText(isSectorsDisabled
			? 'Sectors are disabled, please turn off polygons/boroughs'
			: '',
		);
	}, [isSectorsDisabled]);

	useEffect(() => {
		if (!isEmptyObjectRecursive(appliedSectorsFilters)) {
			setFilters(appliedSectorsFilters);
		}
	}, [appliedSectorsFilters]);

	useEffect(() => {
		setIsSectorsDisabled(areBoroughsEnabled || !!showingCustomPolygonIds.length);
	}, [areBoroughsEnabled, showingCustomPolygonIds.length]);

	const isDirtySectorFilters = useMemo(() => {
		if (isDirtySectorsVisible) return true;

		const preparedTempState = temporaryCheckboxesState.map(item => {
			return {
				value: item.value,
				checked: item.checked,
				nestedItems: item.nestedItems?.map(nestedItem => {
					return {
						value: nestedItem.value,
						checked: nestedItem.checked,
					};
				}),
			};
		});

		const preparedTechnologyCheckboxes = technologyCheckboxes.map(item => {
			return {
				value: item.value,
				checked: item.checked,
				nestedItems: item.nestedItems?.map(nestedItem => {
					return {
						value: nestedItem.value,
						checked: nestedItem.checked,
					};
				}),
			};
		});

		return !isEqual(preparedTempState, preparedTechnologyCheckboxes);
	}, [
		isDirtySectorsVisible,
		technologyCheckboxes,
		temporaryCheckboxesState,
	]);

	return (
		<div
			ref={containerRef}
			className={controlStyles.controlItemContainer}
		>
			<div className={styles.header}>
				<div className={styles.sectionTitle}>Sectors</div>
				<PinAction
					isPinned={isPinned}
					pinTooltip='Pin'
					setIsPinned={setIsPinned}
					tooltipPlacement='left'
					unpinTooltip='Unpin'
				/>
			</div>
			<div className={styles.sectionContainer}>
				<div className={styles.sectorsContainer}>
					<SliderCheckbox
						checked={isSectorsVisible}
						className={styles.sectorsToggle}
						disabled={isSectorsDisabled}
						id='showSectors'
						label='Show sectors'
						name='showSectors'
						onChange={handleChangeSectorsState}
						tooltipText={sectorsDisabledTooltipText}
					/>
				</div>
				<div className={styles.sectionTitle}>
					Technologies
				</div>
				<NestedCheckboxGroup
					className={styles.nestedCheckboxGroup}
					disabled={!isSectorsVisible}
					items={technologyCheckboxes}
					maxHeightOfNestedItems={200}
					onChange={handleChangeSectorsTechnology}
				/>
				<SubmissionButtons
					className={styles.submissionButtonsContainer}
					disabledPositive={!isDirtySectorFilters}
					negativeHandler={handleReset}
					negativeLabel='Reset all'
					positiveHandler={applyFilters}
					positiveLabel='Apply'
				/>
			</div>
		</div>
	);
};

export default memo(Sectors);
